import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject, Type, ViewChild, ViewContainerRef, reflectComponentType } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { BaseDashboard } from '@memberjunction/ng-shared';
import { ResourceData, UserInfoEngine } from '@memberjunction/core-entities';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import {
    marketingBrandConfigurationEntity,
    marketingChannelConfigurationEntity,
    marketingContentStrategyEntity,
    marketingContentStrategyPillarEntity,
    marketingContentPlanEntity,
    marketingContentPlanItemEntity,
    marketingContentTypeEntity,
    marketingContentStatusEntity,
    marketingBlogStatusEntity,
    marketingContentFeedEntity,
    marketingContentTemplateEntity,
    marketingBlogEntity,
    marketingNewsletterEntity,
    marketingSocialPostEntity,
    marketingMarketingRecommendationEntity,
    marketingContentPerformanceMetricEntity,
} from 'mj_generatedentities';

/** Read-only projection of ContentPerformanceMetric — sections only consume, never mutate */
export type PerformanceMetricRead = Pick<
    marketingContentPerformanceMetricEntity,
    'ID' | 'ContentID' | 'ContentTypeID' | 'MetricDate' | 'Platform' | 'PageViews' | 'UniqueVisitors' | 'AvgTimeOnPageSeconds'
>;

import { WorkspaceStateService } from './workspace-state.service';
import {
    filterRoutesByRole,
    findRoute,
    getSubnavRoutes,
    passesRoleGate,
    WORKSPACE_TABS,
    WorkspaceTab,
} from './marketing-workspace.routes';

interface CompanyRow { ID: string; Name: string; }
interface SocialPlatformRow { ID: string; Name: string; }
interface IntegrationRow { ID: string; Name: string; Description: string; }
export interface CompanyIntegrationRow { ID: string; IntegrationID: string; Integration: string; IsActive: boolean; AccessToken: string | null; APIKey: string | null; }
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface MarketingWorkspacePrefs {
    selectedCompanyId?: string;
}

const SECTION_PARAM = 'section';
const COMPANY_PARAM = 'company';
const ID_PARAM = 'id';
const USER_SETTINGS_KEY = 'Marketing.Workspace.UserPreferences';

/**
 * Marketing Workspace dashboard — the unified shell aligned with mockup IA (card #887).
 *
 * Lives alongside the legacy CMODashboard and MarketingDashboard registrations during the
 * migration window; nothing is removed from those. Future cards add real surfaces here as
 * functionality is ported. See marketing-workspace.routes.ts for the section registry.
 */
@Component({
    standalone: false,
    selector: 'mkt-marketing-workspace',
    templateUrl: './marketing-workspace.component.html',
    styleUrls: ['./marketing-workspace.component.css'],
    providers: [WorkspaceStateService],
})
@RegisterClass(BaseDashboard, 'MarketingWorkspace')
export class MarketingWorkspaceComponent extends BaseDashboard implements OnInit, OnDestroy {
    private cdr = inject(ChangeDetectorRef);
    state = inject(WorkspaceStateService);
    private settingsLoadedFromEngine = false;

    readonly allTabs = WORKSPACE_TABS;
    /**
     * Tabs filtered by the current user's role (#938). Hides Admin-only tabs
     * (currently the Admin tab) for Operators / Viewers, and hides everything
     * but `home` if the user has no AuthorizedUser row at all (defense-in-depth
     * — `companies` filtering already short-circuits that case via the
     * unauthorized template branch, but we keep this consistent).
     */
    get tabs(): typeof WORKSPACE_TABS {
        const role = this.state.userRole();
        return WORKSPACE_TABS.filter(t => passesRoleGate({ requiredRole: t.requiredRole }, role));
    }

    // ─── Company state ─────────────────────────────────
    defaultCompanyId = '';
    companies: CompanyRow[] = [];
    /**
     * Per-(company) role projection from `Authorized Users`. Loaded once at
     * `loadCompanies()` time and re-resolved on company change without an
     * extra DB round-trip. `null` means "system / Owner-type user — bypass
     * role gating" (matches the server-side `isSystemUser` rule).
     */
    private authorizedRowsForCurrentUser: Array<{ CompanyID: string; Role: string | null }> = [];
    private isCurrentUserSystem = false;

    // ─── Lookup data (loaded once at boot) ─────────────
    contentTypes: marketingContentTypeEntity[] = [];
    contentStatuses: marketingContentStatusEntity[] = [];
    blogStatuses: marketingBlogStatusEntity[] = [];
    socialPlatforms: SocialPlatformRow[] = [];
    integrations: IntegrationRow[] = [];

    // ─── Company-scoped content data (loaded on company change; flows down to sections) ─
    blogs: marketingBlogEntity[] = [];
    socialPosts: marketingSocialPostEntity[] = [];
    newsletters: marketingNewsletterEntity[] = [];
    recommendations: marketingMarketingRecommendationEntity[] = [];
    brandConfig: marketingBrandConfigurationEntity | null = null;
    channelConfigs: marketingChannelConfigurationEntity[] = [];
    performanceMetrics: PerformanceMetricRead[] = [];
    activeIntegrations: CompanyIntegrationRow[] = [];

    // ─── Settings-scoped data (lazy-loaded when cog opens) ─
    strategy: marketingContentStrategyEntity | null = null;
    strategyPillars: marketingContentStrategyPillarEntity[] = [];
    contentPlan: marketingContentPlanEntity | null = null;
    contentPlanItems: marketingContentPlanItemEntity[] = [];
    contentFeeds: marketingContentFeedEntity[] = [];
    contentTemplates: marketingContentTemplateEntity[] = [];
    companyIntegrations: CompanyIntegrationRow[] = [];

    // ─── UI state ──────────────────────────────────────
    isLoading = true;
    isLoadingContent = false;
    settingsOpen = false;
    settingsLoaded = false;
    saveStatus: Record<string, SaveStatus> = {};

    /** True when we've finished loading companies and the user has zero
     *  authorized companies — the Marketing Agent is not for them. Drives
     *  the access-denied template branch. See issue #916. */
    get unauthorized(): boolean { return !this.isLoading && this.companies.length === 0; }

    get currentRoute() { return this.state.currentRoute(); }
    get currentSectionPath(): string { return this.state.path(); }
    get currentTab(): WorkspaceTab | null { return this.currentRoute?.parent ?? null; }
    get currentSubnavRoutes() {
        if (!this.currentTab) return [];
        return filterRoutesByRole(getSubnavRoutes(this.currentTab), this.state.userRole());
    }
    get currentComponent(): Type<unknown> | null { return this.currentRoute?.component ?? null; }

    private declaredInputCache = new WeakMap<Type<unknown>, Set<string>>();
    private getDeclaredInputs(component: Type<unknown>): Set<string> {
        const cached = this.declaredInputCache.get(component);
        if (cached) return cached;
        const mirror = reflectComponentType(component);
        const names = new Set<string>((mirror?.inputs ?? []).map(i => i.templateName));
        this.declaredInputCache.set(component, names);
        return names;
    }

    get sectionInputs(): Record<string, unknown> {
        const base: Record<string, unknown> = {
            companyId: this.defaultCompanyId,
            companyName: this.companyName,
            // Shared marketing data — sections receive the slice they need (spreadsheet model)
            blogs: this.blogs,
            socialPosts: this.socialPosts,
            newsletters: this.newsletters,
            recommendations: this.recommendations,
            performanceMetrics: this.performanceMetrics,
            activeIntegrations: this.activeIntegrations,
            contentStatuses: this.contentStatuses,
            contentTypes: this.contentTypes,
            blogStatuses: this.blogStatuses,
            socialPlatforms: this.socialPlatforms,
            channelConfigs: this.channelConfigs,
            brandConfig: this.brandConfig,
            // Notify sections that data is still loading
            isLoadingContent: this.isLoadingContent,
            // Navigation callbacks — sections call these to route within the workspace
            // (avoids full-page-reload that <a href="?section=..."> triggers).
            navigate: (path: string) => this.setSection(path),
            navigateWithId: (path: string, id?: string) => this.setSection(path, id),
            selectedId: this.state.selectedId(),
            // Per-section affordance gating (#938) — sections that have edit / approve /
            // reject buttons should hide them when userRole's rank is below the action's
            // requirement. Null = system user (full access) or not-yet-loaded.
            userRole: this.state.userRole(),
        };
        const stub = this.currentRoute?.stub;
        if (stub) {
            base['eyebrow'] = stub.eyebrow;
            base['title'] = stub.title;
            base['sub'] = stub.sub;
            base['csTitle'] = stub.csTitle;
            base['csBody'] = stub.csBody;
            if (stub.csIcon) base['csIcon'] = stub.csIcon;
            if (stub.legacyLabel) base['legacyLabel'] = stub.legacyLabel;
            if (stub.legacyHref) base['legacyHref'] = stub.legacyHref;
        }
        const component = this.currentComponent;
        if (!component) return base;
        const declared = this.getDeclaredInputs(component);
        const filtered: Record<string, unknown> = {};
        for (const key of Object.keys(base)) {
            if (declared.has(key)) filtered[key] = base[key];
        }
        return filtered;
    }
    get companyName(): string {
        const c = this.companies.find(x => x.ID === this.defaultCompanyId);
        return c?.Name ?? '';
    }
    isTabActive(tab: WorkspaceTab): boolean {
        return this.currentTab === tab;
    }

    // ─── BaseDashboard hooks ────────────────────────────
    protected override initDashboard(): void {
        this.NotifyLoadComplete();
    }

    async GetResourceDisplayName(_data: ResourceData): Promise<string> {
        return 'Marketing Workspace';
    }

    async loadData(): Promise<void> {
        await this.loadCompanies();
    }

    override async ngOnInit(): Promise<void> {
        // Resolve initial company + section BEFORE super.ngOnInit() so loadData()
        // fires loadCompanies once with the right company. Priority:
        //   1. ?company= deep-link (overrides stored setting for this visit)
        //   2. UserInfoEngine setting (last user-selected company)
        //   3. fallback to first company in loadCompanies()
        const queryParams = this.GetQueryParams();
        const queryCompanyId = queryParams[COMPANY_PARAM];
        if (queryCompanyId) {
            this.defaultCompanyId = queryCompanyId;
        } else {
            const stored = this.readPrefsFromEngine();
            if (stored?.selectedCompanyId) {
                this.defaultCompanyId = stored.selectedCompanyId;
            }
        }
        const initialSection = this.normalizeSectionParam(queryParams[SECTION_PARAM]);
        this.state.setPath(initialSection);
        this.state.setSelectedId(queryParams[ID_PARAM] || '');
        this.settingsLoadedFromEngine = true;

        await super.ngOnInit();
    }

    /**
     * React to deep-link / browser back-forward query param changes.
     * Reaches us via NavigationService.QueryParamChanged$ subscribed in
     * BaseResourceComponent.ngOnInit() — does NOT write back to UserInfoEngine
     * (browser navigation isn't a deliberate selection).
     */
    protected override OnQueryParamsChanged(params: Record<string, string>, _source: 'popstate' | 'deeplink'): void {
        const nextSection = this.normalizeSectionParam(params[SECTION_PARAM]);
        if (nextSection !== this.state.path()) {
            this.state.setPath(nextSection);
        }
        const nextId = params[ID_PARAM] || '';
        if (nextId !== this.state.selectedId()) {
            this.state.setSelectedId(nextId);
        }
        const nextCompany = params[COMPANY_PARAM];
        if (nextCompany && nextCompany !== this.defaultCompanyId) {
            this.defaultCompanyId = nextCompany;
            this.settingsLoaded = false;
            this.refreshUserRoleForActiveCompany();
            void this.loadContentData();
        }
        this.cdr.detectChanges();
    }

    private normalizeSectionParam(raw: string | undefined): string {
        if (!raw) return 'home';
        return raw.includes('%2F') ? decodeURIComponent(raw) : raw;
    }

    private readPrefsFromEngine(): MarketingWorkspacePrefs | null {
        try {
            const raw = UserInfoEngine.Instance.GetSetting(USER_SETTINGS_KEY);
            if (!raw) return null;
            return JSON.parse(raw) as MarketingWorkspacePrefs;
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[MarketingWorkspace] failed to read user prefs: ${msg}`);
            return null;
        }
    }

    private persistPrefs(): void {
        if (!this.settingsLoadedFromEngine) return; // suppress writes during initial load
        const prefs: MarketingWorkspacePrefs = { selectedCompanyId: this.defaultCompanyId };
        try {
            UserInfoEngine.Instance.SetSettingDebounced(USER_SETTINGS_KEY, JSON.stringify(prefs));
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[MarketingWorkspace] failed to persist user prefs: ${msg}`);
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    // ─── Navigation ─────────────────────────────────────
    setSection(path: string, id?: string): void {
        const route = findRoute(path);
        if (!route) {
            console.warn(`[MarketingWorkspace] Unknown section path: ${path}`);
            return;
        }
        // Defense-in-depth (#938): if a role-gated route is reached via
        // deep-link / bookmark, redirect to home rather than render it.
        // The action layer is the security backstop; this just keeps the UX
        // honest about what the user is allowed to see.
        if (!passesRoleGate(route, this.state.userRole())) {
            console.warn(`[MarketingWorkspace] Role gate denied access to '${path}' for role '${this.state.userRole() ?? 'system'}'`);
            this.state.setPath('home');
            this.state.setSelectedId('');
            this.UpdateQueryParams({ [SECTION_PARAM]: 'home', [ID_PARAM]: null });
            return;
        }
        this.state.setPath(path);
        this.state.setSelectedId(id || '');
        this.UpdateQueryParams({ [SECTION_PARAM]: path, [ID_PARAM]: id || null });
    }

    setTab(tab: WorkspaceTab): void {
        const target = WORKSPACE_TABS.find(t => t.key === tab);
        if (target) this.setSection(target.defaultPath);
    }

    // ─── Settings cog ───────────────────────────────────
    async toggleSettings(): Promise<void> {
        this.settingsOpen = !this.settingsOpen;
        if (this.settingsOpen && !this.settingsLoaded) {
            await this.loadSettingsData();
        }
        this.cdr.detectChanges();
    }

    closeSettings(): void {
        this.settingsOpen = false;
        this.cdr.detectChanges();
    }

    onCompanyChange(): void {
        this.settingsLoaded = false; // force reload when re-opening settings for new company
        // Persist to UserInfoEngine (debounced) and mirror to ?company= so the URL is shareable.
        this.persistPrefs();
        this.UpdateQueryParams({ [COMPANY_PARAM]: this.defaultCompanyId || null });
        this.refreshUserRoleForActiveCompany();
        this.loadContentData();
        if (this.settingsOpen) {
            this.loadSettingsData();
        }
    }

    /**
     * Resolve the active user's role for `defaultCompanyId` from the cached
     * AuthorizedUser rows and push it onto WorkspaceStateService so sub-nav
     * filters + section role-gates re-evaluate. Cheap — no I/O. See #938.
     */
    private refreshUserRoleForActiveCompany(): void {
        if (this.isCurrentUserSystem) {
            this.state.setUserRole(null);
            return;
        }
        if (!this.defaultCompanyId) {
            this.state.setUserRole(null);
            return;
        }
        const target = this.defaultCompanyId.toLowerCase();
        const row = this.authorizedRowsForCurrentUser.find(r => r.CompanyID.toLowerCase() === target);
        if (!row) {
            // No AuthorizedUser row for this (user, company). Treat as 'Viewer'
            // so role-gated routes hide. The unauthorized template branch
            // already short-circuits the case where the user has zero rows
            // anywhere; this is a safety fallback.
            this.state.setUserRole('Viewer');
            return;
        }
        const r = row.Role;
        if (r === 'Admin' || r === 'Operator' || r === 'Viewer') {
            this.state.setUserRole(r);
        } else {
            // Default-deny posture matches access.ts loadAuthorizedRow's
            // fallback: an unrecognized / null role coerces to 'Operator'.
            this.state.setUserRole('Operator');
        }
    }

    // ─── Data Loading ───────────────────────────────────
    private async loadCompanies(): Promise<void> {
        this.isLoading = true;
        try {
            const rv = new RunView();
            const md = new Metadata();
            const currentUserId = md.CurrentUser?.ID ?? '';
            const [companies, types, contentStatuses, blogStatuses, socialPlatforms, integrations, authorized] = await rv.RunViews([
                { EntityName: 'MJ: Companies', ResultType: 'simple', Fields: ['ID', 'Name'], MaxRows: 200, OrderBy: 'Name' },
                { EntityName: 'Content Types', ExtraFilter: 'IsActive=1', ResultType: 'entity_object' },
                { EntityName: 'Content Status', ResultType: 'entity_object' },
                { EntityName: 'Blog Status', ExtraFilter: 'IsActive=1', ResultType: 'entity_object' },
                { EntityName: 'Social Platforms', ResultType: 'simple', Fields: ['ID', 'Name'] },
                { EntityName: 'MJ: Integrations', ResultType: 'simple', Fields: ['ID', 'Name', 'Description'] },
                // Marketing permission allow-list (issue #916). RLS on AuthorizedUser is Self-Only,
                // so this implicitly returns only the current user's rows even without the filter,
                // but we keep the explicit filter for clarity and as a defense-in-depth check.
                // Role is also pulled here (#938) so sub-nav filtering can resolve the active
                // role for the selected company without an extra round-trip.
                { EntityName: 'Authorized Users', ExtraFilter: `UserID='${currentUserId}'`, ResultType: 'simple', Fields: ['ID', 'CompanyID', 'Role'], MaxRows: 200 },
            ]);
            // Detect system-identity users (Owner-type or scheduled-job runner)
            // — they bypass per-company role gating in the workspace, mirroring
            // the server-side `isSystemUser` check in packages/Marketing/src/utils/access.ts.
            const cu = md.CurrentUser;
            this.isCurrentUserSystem = !!cu && (
                cu.Type === 'Owner' ||
                (cu.Name === 'System' && cu.Email === 'not.set@nowhere.com')
            );
            if (companies.Success && authorized.Success) {
                const allCompanies = companies.Results as CompanyRow[];
                this.authorizedRowsForCurrentUser = (authorized.Results as Array<{ CompanyID: string; Role: string | null }>);
                if (this.isCurrentUserSystem) {
                    // System users see every company — no filter applied.
                    this.companies = allCompanies;
                } else {
                    const allowedCompanyIds = new Set<string>(
                        this.authorizedRowsForCurrentUser.map(r => r.CompanyID.toLowerCase())
                    );
                    this.companies = allCompanies.filter(c => allowedCompanyIds.has(c.ID.toLowerCase()));
                }
                if (!this.defaultCompanyId && this.companies.length > 0) {
                    this.defaultCompanyId = this.companies[0].ID;
                } else if (this.companies.length === 0) {
                    LogError(`[MarketingWorkspace] User ${currentUserId} has no AuthorizedUser rows — Marketing access denied.`);
                }
                // Resolve the role for the now-selected company so sub-nav filters correctly.
                this.refreshUserRoleForActiveCompany();
            }
            if (types.Success) this.contentTypes = types.Results as marketingContentTypeEntity[];
            if (contentStatuses.Success) this.contentStatuses = contentStatuses.Results as marketingContentStatusEntity[];
            if (blogStatuses.Success) this.blogStatuses = blogStatuses.Results as marketingBlogStatusEntity[];
            if (socialPlatforms.Success) this.socialPlatforms = socialPlatforms.Results as SocialPlatformRow[];
            if (integrations.Success) this.integrations = integrations.Results as IntegrationRow[];
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[MarketingWorkspace] loadCompanies failed: ${msg}`);
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
            // Kick off content load now that we have a company selected
            if (this.defaultCompanyId) await this.loadContentData();
        }
    }

    /**
     * Load company-scoped content data (blogs / social / newsletters / brand / channels).
     * Sections receive this via inputs — single source of truth for the spreadsheet model.
     */
    private async loadContentData(): Promise<void> {
        if (!this.defaultCompanyId) return;
        const cid = this.defaultCompanyId;
        this.isLoadingContent = true;
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const [blogs, socialPosts, newsletters, brand, channels, recs, metrics, integrations] = await rv.RunViews([
                { EntityName: 'Blogs', ExtraFilter: `CompanyID='${cid}'`, OrderBy: '__mj_CreatedAt DESC', MaxRows: 200, ResultType: 'entity_object' },
                { EntityName: 'Social Posts', ExtraFilter: `CompanyID='${cid}'`, OrderBy: '__mj_CreatedAt DESC', MaxRows: 200, ResultType: 'entity_object' },
                { EntityName: 'Newsletters', ExtraFilter: `CompanyID='${cid}'`, OrderBy: '__mj_CreatedAt DESC', MaxRows: 100, ResultType: 'entity_object' },
                { EntityName: 'Brand Configurations', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, MaxRows: 1, ResultType: 'entity_object' },
                { EntityName: 'Channel Configurations', ExtraFilter: `CompanyID='${cid}'`, ResultType: 'entity_object' },
                { EntityName: 'Marketing Recommendations', ExtraFilter: `CompanyID='${cid}' AND Status='Open'`, OrderBy: 'Category ASC, __mj_CreatedAt DESC', ResultType: 'entity_object' },
                // Last 14d so sections can compute 7d window vs prior-7d baseline
                { EntityName: 'Content Performance Metrics', ExtraFilter: `CompanyID='${cid}' AND MetricDate >= DATEADD(day, -14, GETDATE())`, OrderBy: 'MetricDate DESC', MaxRows: 10000, ResultType: 'simple' },
                // Eager-loaded so the Analytics > Data Sources section can render without a settings-drawer roundtrip
                { EntityName: 'MJ: Company Integrations', ExtraFilter: `CompanyID='${cid}'`, ResultType: 'simple', Fields: ['ID', 'IntegrationID', 'Integration', 'IsActive', 'AccessToken', 'APIKey'] },
            ]);
            if (blogs.Success) this.blogs = blogs.Results as marketingBlogEntity[];
            if (socialPosts.Success) this.socialPosts = socialPosts.Results as marketingSocialPostEntity[];
            if (newsletters.Success) this.newsletters = newsletters.Results as marketingNewsletterEntity[];
            if (brand.Success) this.brandConfig = (brand.Results?.[0] as marketingBrandConfigurationEntity) ?? null;
            if (channels.Success) this.channelConfigs = channels.Results as marketingChannelConfigurationEntity[];
            if (recs.Success) this.recommendations = recs.Results as marketingMarketingRecommendationEntity[];
            if (metrics.Success) this.performanceMetrics = metrics.Results as PerformanceMetricRead[];
            if (integrations.Success) this.activeIntegrations = integrations.Results as CompanyIntegrationRow[];
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[MarketingWorkspace] loadContentData failed: ${msg}`);
        } finally {
            this.isLoadingContent = false;
            this.cdr.detectChanges();
        }
    }

    /** Re-load shared content (e.g., after a section saves a blog status change). */
    async reloadContent(): Promise<void> {
        await this.loadContentData();
    }

    private async loadSettingsData(): Promise<void> {
        if (!this.defaultCompanyId) return;
        const cid = this.defaultCompanyId;
        try {
            const rv = new RunView();
            // Settings-specific data only — content types / social platforms / brand / channels
            // are already loaded by loadCompanies + loadContentData.
            const queries = [
                { EntityName: 'Content Strategies', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, MaxRows: 1, ResultType: 'entity_object' as const },
                { EntityName: 'Content Strategy Pillars', ExtraFilter: `ContentStrategyID IN (SELECT ID FROM [marketing].[vwContentStrategies] WHERE CompanyID='${cid}' AND IsActive=1)`, OrderBy: 'Priority ASC', ResultType: 'entity_object' as const },
                { EntityName: 'Content Plans', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, MaxRows: 1, ResultType: 'entity_object' as const },
                { EntityName: 'Content Plan Items', ExtraFilter: `ContentPlanID IN (SELECT ID FROM [marketing].[vwContentPlans] WHERE CompanyID='${cid}' AND IsActive=1) AND IsActive=1`, ResultType: 'entity_object' as const },
                { EntityName: 'Content Feeds', ExtraFilter: `CompanyID='${cid}'`, ResultType: 'entity_object' as const },
                { EntityName: 'Content Templates', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, ResultType: 'entity_object' as const },
                { EntityName: 'MJ: Company Integrations', ExtraFilter: `CompanyID='${cid}'`, ResultType: 'simple' as const, Fields: ['ID', 'IntegrationID', 'Integration', 'IsActive', 'AccessToken', 'APIKey'] },
            ];
            const results = await rv.RunViews(queries);
            if (results[0].Success) this.strategy = (results[0].Results?.[0] as marketingContentStrategyEntity) ?? null;
            if (results[1].Success) this.strategyPillars = results[1].Results as marketingContentStrategyPillarEntity[];
            if (results[2].Success) this.contentPlan = (results[2].Results?.[0] as marketingContentPlanEntity) ?? null;
            if (results[3].Success) this.contentPlanItems = results[3].Results as marketingContentPlanItemEntity[];
            if (results[4].Success) this.contentFeeds = results[4].Results as marketingContentFeedEntity[];
            if (results[5].Success) this.contentTemplates = results[5].Results as marketingContentTemplateEntity[];
            if (results[6].Success) this.companyIntegrations = results[6].Results as CompanyIntegrationRow[];
            this.settingsLoaded = true;
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[MarketingWorkspace] loadSettingsData failed: ${msg}`);
        } finally {
            this.cdr.detectChanges();
        }
    }

    // ─── Settings save handlers (port from CMODashboard) ─
    private async withSaveStatus(key: string, op: () => Promise<boolean>): Promise<void> {
        this.saveStatus = { ...this.saveStatus, [key]: 'saving' };
        this.cdr.detectChanges();
        try {
            const ok = await op();
            this.saveStatus = { ...this.saveStatus, [key]: ok ? 'saved' : 'error' };
            this.cdr.detectChanges();
            if (ok) setTimeout(() => { this.saveStatus = { ...this.saveStatus, [key]: 'idle' }; this.cdr.detectChanges(); }, 2000);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[MarketingWorkspace] save '${key}' failed: ${msg}`);
            this.saveStatus = { ...this.saveStatus, [key]: 'error' };
            this.cdr.detectChanges();
        }
    }

    async saveBrandConfig(): Promise<void> {
        if (!this.brandConfig) return;
        await this.withSaveStatus('brandConfig', () => this.brandConfig!.Save());
    }

    async saveStrategy(): Promise<void> {
        if (!this.strategy) return;
        await this.withSaveStatus('strategy', () => this.strategy!.Save());
    }

    async savePillar(pillar: marketingContentStrategyPillarEntity): Promise<void> {
        await this.withSaveStatus(`pillar-${pillar.ID}`, () => pillar.Save());
    }

    async addPillar(): Promise<void> {
        if (!this.strategy) return;
        const md = new Metadata();
        const pillar = await md.GetEntityObject<marketingContentStrategyPillarEntity>('Content Strategy Pillars');
        pillar.NewRecord();
        pillar.ContentStrategyID = this.strategy.ID;
        pillar.Name = 'New Pillar';
        pillar.Priority = (this.strategyPillars.length + 1);
        const ok = await pillar.Save();
        if (ok) {
            this.strategyPillars = [...this.strategyPillars, pillar];
            this.cdr.detectChanges();
        }
    }

    async deletePillar(event: { pillar: marketingContentStrategyPillarEntity; index: number }): Promise<void> {
        const ok = await event.pillar.Delete();
        if (ok) {
            this.strategyPillars = this.strategyPillars.filter(p => p.ID !== event.pillar.ID);
            this.cdr.detectChanges();
        }
    }

    async savePlanItem(item: marketingContentPlanItemEntity): Promise<void> {
        await this.withSaveStatus(`plan-item-${item.ID}`, () => item.Save());
    }

    async saveChannelConfig(config: marketingChannelConfigurationEntity): Promise<void> {
        await this.withSaveStatus(`channel-${config.ID}`, () => config.Save());
    }

    async addChannelConfig(data: { contentTypeId: string; socialPlatformId: string | null; requiresApproval: boolean; autoPublish: boolean; maxPerWeek: number | null }): Promise<void> {
        const md = new Metadata();
        const entity = await md.GetEntityObject<marketingChannelConfigurationEntity>('Channel Configurations');
        entity.NewRecord();
        entity.CompanyID = this.defaultCompanyId;
        entity.ContentTypeID = data.contentTypeId;
        if (data.socialPlatformId) entity.SocialPlatformID = data.socialPlatformId;
        entity.RequiresApproval = data.requiresApproval;
        entity.AutoPublish = data.autoPublish;
        if (data.maxPerWeek != null) entity.MaxPerWeek = data.maxPerWeek;
        const ok = await entity.Save();
        if (ok) {
            this.channelConfigs = [...this.channelConfigs, entity];
            this.cdr.detectChanges();
        }
    }

    async deleteChannelConfig(configId: string): Promise<void> {
        const config = this.channelConfigs.find(c => c.ID === configId);
        if (!config) return;
        const ok = await config.Delete();
        if (ok) {
            this.channelConfigs = this.channelConfigs.filter(c => c.ID !== configId);
            this.cdr.detectChanges();
        }
    }

    async saveFeed(feed: marketingContentFeedEntity): Promise<void> {
        await this.withSaveStatus(`feed-${feed.ID}`, () => feed.Save());
    }

    async saveTemplate(template: marketingContentTemplateEntity): Promise<void> {
        await this.withSaveStatus(`template-${template.ID}`, () => template.Save());
    }

    async createTemplate(data: { name: string; templateType: string }): Promise<void> {
        const md = new Metadata();
        const entity = await md.GetEntityObject<marketingContentTemplateEntity>('Content Templates');
        entity.NewRecord();
        entity.CompanyID = this.defaultCompanyId;
        entity.Name = data.name;
        entity.TemplateType = data.templateType;
        entity.IsActive = true;
        const ok = await entity.Save();
        if (ok) {
            this.contentTemplates = [...this.contentTemplates, entity];
            this.cdr.detectChanges();
        }
    }

    async addCompanyIntegration(data: { integrationId: string; accessToken?: string; apiKey?: string }): Promise<void> {
        const md = new Metadata();
        const entity = await md.GetEntityObject('MJ: Company Integrations');
        entity.NewRecord();
        entity.Set('CompanyID', this.defaultCompanyId);
        entity.Set('IntegrationID', data.integrationId);
        entity.Set('IsActive', true);
        entity.Set('IsExternalSystemReadOnly', false);
        entity.Set('ScheduleEnabled', false);
        entity.Set('ScheduleType', 'Manual');
        if (data.accessToken) entity.Set('AccessToken', data.accessToken);
        if (data.apiKey) entity.Set('APIKey', data.apiKey);
        const ok = await entity.Save();
        if (ok) await this.loadSettingsData();
    }

    async toggleCompanyIntegration(ciId: string): Promise<void> {
        const ci = this.companyIntegrations.find(c => c.ID === ciId);
        if (!ci) return;
        const rv = new RunView();
        const result = await rv.RunView({ EntityName: 'MJ: Company Integrations', ExtraFilter: `ID='${ciId}'`, ResultType: 'entity_object' });
        if (!result.Success) {
            LogError(`[MarketingWorkspace] toggleCompanyIntegration lookup failed for ${ciId}: ${result.ErrorMessage}`);
            return;
        }
        if (result.Results.length === 0) {
            LogError(`[MarketingWorkspace] toggleCompanyIntegration: no Company Integration row for ID=${ciId}`);
            return;
        }
        const entity = result.Results[0];
        entity.Set('IsActive', !ci.IsActive);
        if (!await entity.Save()) {
            LogError(`[MarketingWorkspace] toggleCompanyIntegration save failed for ${ciId}: ${entity.LatestResult?.Message ?? 'unknown error'}`);
        }
        await this.loadSettingsData();
    }

    async deleteCompanyIntegration(ciId: string): Promise<void> {
        const rv = new RunView();
        const result = await rv.RunView({ EntityName: 'MJ: Company Integrations', ExtraFilter: `ID='${ciId}'`, ResultType: 'entity_object' });
        if (!result.Success) {
            LogError(`[MarketingWorkspace] deleteCompanyIntegration lookup failed for ${ciId}: ${result.ErrorMessage}`);
            return;
        }
        if (result.Results.length === 0) {
            LogError(`[MarketingWorkspace] deleteCompanyIntegration: no Company Integration row for ID=${ciId}`);
            return;
        }
        if (!await result.Results[0].Delete()) {
            LogError(`[MarketingWorkspace] deleteCompanyIntegration delete failed for ${ciId}: ${result.Results[0].LatestResult?.Message ?? 'unknown error'}`);
        }
        await this.loadSettingsData();
    }
}

/**
 * Anti-tree-shake hook so a consumer can `import { LoadMarketingWorkspace } from 'marketing-ui'`
 * to ensure registration runs.
 */
export function LoadMarketingWorkspace(): void { /* keep registration alive */ }
